import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

interface CodeCopyButtonProps {
  code: string;
  /**
   * "overlay" sits in the block's top-right, "sticky" stays in view while a
   * filling block scrolls, "inline" sits in a header row.
   */
  placement?: "overlay" | "sticky" | "inline";
}

const COPY_LABEL = "Code kopieren";
const COPIED_LABEL = "Code kopiert";

const renderCopyIcon = (copied: boolean) => {
  if (copied) return <Check className="h-3.5 w-3.5" aria-hidden="true" />;
  return <Copy className="h-3.5 w-3.5" aria-hidden="true" />;
};

const placementClassName = (placement: CodeCopyButtonProps["placement"]) => {
  if (placement === "sticky") return "sticky top-2 float-right mr-2 -mb-8";
  if (placement === "inline") return "shrink-0 bg-gray-600";
  return "absolute top-2 right-2";
};

/**
 * Copies a snippet to the clipboard — the control in the corner of an inline
 * code example and of a code-quiz editor.
 */
const CodeCopyButton = ({
  code,
  placement = "overlay",
}: CodeCopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
    });
  };

  const resetCopiedLabel = () => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  };
  useEffect(resetCopiedLabel, [copied]);

  const label = copied ? COPIED_LABEL : COPY_LABEL;

  return (
    <button
      type="button"
      onClick={copyCode}
      className={cn(
        "z-10 cursor-pointer rounded bg-gray-700 p-1.5 text-gray-300 transition-colors hover:bg-gray-600 hover:text-white",
        placementClassName(placement)
      )}
      title={label}
      aria-label={label}
    >
      {renderCopyIcon(copied)}
    </button>
  );
};

export default CodeCopyButton;
